/**
 * The Survivor rules draft a Commissioner edits on the create-pool rules step,
 * the change-rules screen and the New contest form, on web and on mobile
 * (ISSUE-1312). One copy, so the same answers build the same contest config on
 * both clients and the same fixture produces the same rule snapshot.
 *
 * This is form logic, not a contest rule: it turns what a fan typed into a
 * config the server validates again (`validateContestConfig`, contest-api).
 * It is reached through the `@sportsmet/contest-engine/rules-draft` subpath,
 * not the engine barrel, so it never sits on the reserved rule-name list.
 */
import { countInclusiveDays } from "./validateContestConfig";
import {
  validatePickSchedule,
  type PickSchedule,
} from "../rounds/pickSchedule";
import {
  DEFAULT_WEEK_START_DAY,
  type typeAdvanceCriterion,
  type typeContestTiebreaker,
  type typeEndDateMode,
  type typePickBasis,
  type typePostponementPolicy,
  type typeRevivesAllowedMode,
  type typeRoundGrouping,
  type typeRoundLockTiming,
  type typeSpreadTiming,
  type typeTiePolicy,
  type typeWeekStartDay,
  WEEK_START_DAYS,
} from "../domain/contestConfig";
import { DEFAULT_SURVIVOR_CONTEST_DAYS } from "../domain/defaultContestConfig";

const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;

export const END_DATE_ORDER_ERROR =
  "The end date has to be on or after the start date.";

export type typeSurvivorContestConfig = {
  pickSchedule?: PickSchedule | null;
  startDate: string;
  /** Null while the end date is TBD (ISSUE-1095); contest-api fills it. */
  endDate: string | null;
  endDateMode: typeEndDateMode;
  roundGrouping: typeRoundGrouping;
  weekStartDay?: number | null;
  roundLockTiming: typeRoundLockTiming;
  pickBasis: typePickBasis;
  spreadTiming?: typeSpreadTiming | null;
  spreadPolicy?: "automatic72h";
  advanceCriterion: typeAdvanceCriterion;
  livesAllowed: number;
  requiredPicksPerRound: number;
  maxEntriesPerMember: number;
  teamReuseAllowed: boolean;
  teamReuseLockoutDays?: number | null;
  teamReuseMaxCount?: number | null;
  revivesAllowed: typeRevivesAllowedMode;
  revivesAllowedCount?: number | null;
  postponementPolicy: typePostponementPolicy;
  tiePolicy: typeTiePolicy;
  tiebreaker: typeContestTiebreaker;
  winPoints: number;
  lossPoints: number;
  skipsAllowed: boolean;
  skipsMaxPerContest?: number | null;
};

/**
 * The rules a fan is editing, which is not the same thing as a valid contest
 * config. Every numeric field is nullable because null is what the draft holds
 * while a fan has the field emptied out mid-edit: they cleared it and have not
 * typed the new number yet. `buildContestConfig` is the gate that turns a draft
 * into a config, and it refuses a draft with a required number still missing,
 * so an emptied field blocks Continue instead of quietly defaulting.
 *
 * For `maxMembers` and `teamReuseMaxCount`, null is a real answer rather than a
 * gap: it means unlimited.
 */
export type typeSurvivorRulesDraft = {
  pickSchedule?: PickSchedule | null;
  startDate: string;
  /** "" while endDateMode is "tbd" (ISSUE-1095). */
  endDate: string;
  endDateMode: typeEndDateMode;
  roundGrouping: typeRoundGrouping;
  weekStartDay: typeWeekStartDay;
  roundLockTiming: typeRoundLockTiming;
  requiredPicksPerRound: number | null;
  pickBasis: typePickBasis;
  spreadTiming: typeSpreadTiming;
  teamReuseAllowed: boolean;
  teamReuseLockoutDays: number | null;
  teamReuseMaxCount: number | null;
  advanceCriterion: typeAdvanceCriterion;
  livesAllowed: number | null;
  skipsCount: number | null;
  tiePolicy: typeTiePolicy | null;
  postponementPolicy: typePostponementPolicy;
  maxMembers: number | null;
  maxEntriesPerMember: number | null;
  revivesAllowed: typeRevivesAllowedMode;
  revivesAllowedCount: number | null;
  tiebreaker: typeContestTiebreaker;
  winPoints: number | null;
  lossPoints: number | null;
};

export type typeParseUnlimitedIntResult =
  { ok: true; value: number | null } | { ok: false };

export function defaultContestDateRange(now: Date): {
  startDate: string;
  endDate: string;
} {
  const year = now.getUTCFullYear();
  const month = now.getUTCMonth();
  const day = now.getUTCDate();
  const startDate = new Date(Date.UTC(year, month, day))
    .toISOString()
    .slice(0, 10);
  const endDate = new Date(Date.UTC(year, month, day + 119))
    .toISOString()
    .slice(0, 10);
  return { startDate, endDate };
}

export function emptySurvivorRulesDraft(now: Date): typeSurvivorRulesDraft {
  const dates = defaultContestDateRange(now);
  return {
    startDate: dates.startDate,
    // ISSUE-1095: a new contest runs until one survivor is left or the season
    // ends. "Pick a date" fills a date in (chooseEndDateMode).
    endDate: "",
    endDateMode: "tbd",
    roundGrouping: "week",
    weekStartDay: DEFAULT_WEEK_START_DAY,
    roundLockTiming: "lastGame",
    requiredPicksPerRound: 1,
    pickBasis: "moneyline",
    spreadTiming: "live",
    teamReuseAllowed: false,
    teamReuseLockoutDays: 0,
    teamReuseMaxCount: null,
    advanceCriterion: "winAll",
    livesAllowed: 1,
    skipsCount: 0,
    // ISSUE-1095: the owner's default of 2026-10-08. A fan can still switch it.
    tiePolicy: "tieIsLoss",
    postponementPolicy: "makeUpNextRound",
    maxMembers: null,
    maxEntriesPerMember: 1,
    revivesAllowed: "unlimited",
    revivesAllowedCount: null,
    tiebreaker: "splitMedal",
    winPoints: 1,
    lossPoints: 0,
  };
}

export const EMPTY_SURVIVOR_RULES_DRAFT: typeSurvivorRulesDraft = {
  ...emptySurvivorRulesDraft(new Date("2026-09-01T12:00:00.000Z")),
  startDate: "",
  endDate: "",
};

/** ISSUE-1095. Switches the end date between TBD and a picked date. */
export function chooseEndDateMode(
  rules: typeSurvivorRulesDraft,
  mode: typeEndDateMode,
): typeSurvivorRulesDraft {
  if (mode === "tbd") {
    return { ...rules, endDateMode: "tbd", endDate: "" };
  }
  return {
    ...rules,
    endDateMode: "date",
    endDate:
      rules.endDate !== ""
        ? rules.endDate
        : addUtcDays(rules.startDate, DEFAULT_SURVIVOR_CONTEST_DAYS - 1),
  };
}

export function isEndDateBeforeStart(
  startDate: string,
  endDate: string,
): boolean {
  if (!ISO_DATE.test(startDate) || !ISO_DATE.test(endDate)) return false;
  return endDate < startDate;
}

export function parseUnlimitedInt(raw: string): typeParseUnlimitedIntResult {
  const trimmed = raw.trim();
  if (trimmed === "" || trimmed === "Unlimited") {
    return { ok: true, value: null };
  }
  const parsed = Number.parseInt(trimmed, 10);
  if (!Number.isInteger(parsed) || String(parsed) !== trimmed) {
    return { ok: false };
  }
  return { ok: true, value: parsed };
}

function isIsoDate(value: string): boolean {
  return ISO_DATE.test(value);
}

export function missingSurvivorRulesItems(
  rules: typeSurvivorRulesDraft,
): readonly string[] {
  const missing: string[] = [];
  if (rules.tiePolicy === null) missing.push("a tie rule");
  if (!isIsoDate(rules.startDate)) missing.push("a start date");
  if (rules.endDateMode === "date") {
    if (!isIsoDate(rules.endDate)) missing.push("an end date");
    if (isEndDateBeforeStart(rules.startDate, rules.endDate)) {
      missing.push("an end date on or after the start date");
    }
  }
  if (
    rules.requiredPicksPerRound === null ||
    !Number.isInteger(rules.requiredPicksPerRound) ||
    rules.requiredPicksPerRound < 1
  ) {
    missing.push("picks per round");
  }
  if (
    rules.maxEntriesPerMember === null ||
    !Number.isInteger(rules.maxEntriesPerMember) ||
    rules.maxEntriesPerMember < 1
  ) {
    missing.push("entries per member");
  }
  if (
    rules.winPoints === null ||
    !Number.isInteger(rules.winPoints) ||
    rules.winPoints < 0
  ) {
    missing.push("points for a win");
  }
  if (rules.lossPoints === null || !Number.isInteger(rules.lossPoints)) {
    missing.push("points for a loss");
  }
  if (
    rules.maxMembers !== null &&
    (!Number.isInteger(rules.maxMembers) || rules.maxMembers < 1)
  ) {
    missing.push("a max members number of 1 or more");
  }
  if (
    rules.roundGrouping === "week" &&
    (rules.weekStartDay < 1 || rules.weekStartDay > 7)
  ) {
    missing.push("a week start day");
  }
  if (
    rules.revivesAllowed === "limited" &&
    (rules.revivesAllowedCount === null ||
      !Number.isInteger(rules.revivesAllowedCount) ||
      rules.revivesAllowedCount < 1)
  ) {
    missing.push("revives allowed");
  }
  if (
    rules.revivesAllowed === "unlimited" &&
    rules.revivesAllowedCount !== null
  ) {
    missing.push("revives allowed");
  }
  if (
    rules.teamReuseAllowed &&
    (rules.teamReuseLockoutDays === null ||
      !Number.isInteger(rules.teamReuseLockoutDays) ||
      rules.teamReuseLockoutDays < 0)
  ) {
    missing.push("days before a team can be reused");
  }
  if (
    rules.teamReuseAllowed &&
    rules.teamReuseMaxCount !== null &&
    (!Number.isInteger(rules.teamReuseMaxCount) || rules.teamReuseMaxCount < 1)
  ) {
    missing.push("a team reuse cap of 1 or more");
  }
  if (
    validatePickSchedule(
      rules.pickSchedule,
      rules.endDateMode === "date" ? rules.endDate : null,
      rules.startDate,
    ).length > 0
  )
    missing.push("a valid, non-overlapping pick schedule");
  return missing;
}

export function buildContestConfig(
  rules: typeSurvivorRulesDraft,
): typeSurvivorContestConfig | null {
  if (missingSurvivorRulesItems(rules).length > 0) return null;

  const tiePolicy = rules.tiePolicy;
  if (tiePolicy === null) return null;
  const livesAllowed = 1;
  const requiredPicksPerRound = rules.requiredPicksPerRound;
  if (requiredPicksPerRound === null) return null;
  const maxEntriesPerMember = rules.maxEntriesPerMember;
  if (maxEntriesPerMember === null) return null;
  const winPoints = rules.winPoints;
  if (winPoints === null) return null;
  const lossPoints = rules.lossPoints;
  if (lossPoints === null) return null;
  const skipsAllowed = false;
  return {
    ...(rules.pickSchedule !== undefined
      ? { pickSchedule: rules.pickSchedule }
      : {}),
    startDate: rules.startDate,
    endDate: rules.endDateMode === "tbd" ? null : rules.endDate,
    endDateMode: rules.endDateMode,
    roundGrouping: rules.roundGrouping,
    weekStartDay: rules.roundGrouping === "week" ? rules.weekStartDay : null,
    roundLockTiming: rules.roundLockTiming,
    pickBasis: rules.pickBasis,
    spreadTiming: null,
    ...(rules.pickBasis === "spread"
      ? { spreadPolicy: "automatic72h" as const }
      : {}),
    advanceCriterion: rules.advanceCriterion,
    livesAllowed,
    requiredPicksPerRound,
    maxEntriesPerMember,
    teamReuseAllowed: rules.teamReuseAllowed,
    teamReuseLockoutDays: rules.teamReuseAllowed
      ? rules.teamReuseLockoutDays
      : null,
    teamReuseMaxCount: rules.teamReuseAllowed ? rules.teamReuseMaxCount : null,
    revivesAllowed: rules.revivesAllowed,
    revivesAllowedCount:
      rules.revivesAllowed === "limited" ? rules.revivesAllowedCount : null,
    postponementPolicy: rules.postponementPolicy,
    tiePolicy,
    tiebreaker: rules.tiebreaker,
    winPoints,
    lossPoints,
    skipsAllowed,
    skipsMaxPerContest: null,
  };
}

export function isRulesComplete(rules: typeSurvivorRulesDraft): boolean {
  return buildContestConfig(rules) !== null;
}

/** Adds whole days to a YYYY-MM-DD day in UTC; "" for an unreadable day (ISSUE-1042). */
export function addUtcDays(isoDay: string, days: number): string {
  const parsed = new Date(`${isoDay}T00:00:00.000Z`);
  if (Number.isNaN(parsed.getTime())) return "";
  return new Date(
    Date.UTC(
      parsed.getUTCFullYear(),
      parsed.getUTCMonth(),
      parsed.getUTCDate() + days,
    ),
  )
    .toISOString()
    .slice(0, 10);
}

/** Dates for the next contest: the day after the previous one ended (never before today), same length (ISSUE-1042). */
export function nextContestDateRange(input: {
  previousStartDate: string;
  previousEndDate: string;
  today: string;
}): { startDate: string; endDate: string } {
  const counted = countInclusiveDays({
    startDate: input.previousStartDate,
    endDate: input.previousEndDate,
  });
  const length = counted < 1 ? DEFAULT_SURVIVOR_CONTEST_DAYS : counted;
  const dayAfter = addUtcDays(input.previousEndDate, 1);
  const startDate = dayAfter > input.today ? dayAfter : input.today;
  const endDate = addUtcDays(startDate, length - 1);
  return { startDate, endDate };
}

/** Turns a stored contest config back into the wizard's editable rules draft (ISSUE-1042). */
export function rulesDraftFromContestConfig(
  config: typeSurvivorContestConfig,
): typeSurvivorRulesDraft {
  const weekStartDay =
    typeof config.weekStartDay === "number" &&
    (WEEK_START_DAYS as readonly number[]).includes(config.weekStartDay)
      ? (config.weekStartDay as typeWeekStartDay)
      : DEFAULT_WEEK_START_DAY;
  return {
    ...(config.pickSchedule !== undefined
      ? { pickSchedule: config.pickSchedule }
      : {}),
    startDate: config.startDate,
    endDate: config.endDateMode === "tbd" ? "" : (config.endDate ?? ""),
    endDateMode: config.endDateMode,
    roundGrouping: config.roundGrouping,
    weekStartDay,
    roundLockTiming: config.roundLockTiming,
    requiredPicksPerRound: config.requiredPicksPerRound,
    pickBasis: config.pickBasis,
    spreadTiming: config.spreadTiming ?? "live",
    teamReuseAllowed: config.teamReuseAllowed,
    teamReuseLockoutDays: config.teamReuseLockoutDays ?? 0,
    teamReuseMaxCount: config.teamReuseMaxCount ?? null,
    advanceCriterion: config.advanceCriterion,
    livesAllowed: 1,
    skipsCount: 0,
    tiePolicy: config.tiePolicy,
    postponementPolicy: config.postponementPolicy,
    maxMembers: null,
    maxEntriesPerMember: config.maxEntriesPerMember,
    revivesAllowed: config.revivesAllowed,
    revivesAllowedCount:
      config.revivesAllowed === "limited"
        ? (config.revivesAllowedCount ?? null)
        : null,
    tiebreaker: config.tiebreaker,
    winPoints: config.winPoints,
    lossPoints: config.lossPoints,
  };
}

/** Every config key the change-rules screen may send to /contest/update (ISSUE-1041). */
export const CONTEST_RULES_PATCH_KEYS: readonly (keyof typeSurvivorContestConfig)[] =
  [
    "pickSchedule",
    "spreadPolicy",
    "startDate",
    "endDate",
    "endDateMode",
    "roundGrouping",
    "weekStartDay",
    "roundLockTiming",
    "pickBasis",
    "spreadTiming",
    "advanceCriterion",
    "livesAllowed",
    "requiredPicksPerRound",
    "maxEntriesPerMember",
    "teamReuseAllowed",
    "teamReuseLockoutDays",
    "teamReuseMaxCount",
    "revivesAllowed",
    "revivesAllowedCount",
    "postponementPolicy",
    "tiePolicy",
    "tiebreaker",
    "winPoints",
    "lossPoints",
    "skipsAllowed",
    "skipsMaxPerContest",
  ];

export type typeContestRulesPatch = Partial<
  Omit<typeSurvivorContestConfig, "spreadPolicy">
> & { spreadPolicy?: "automatic72h" | null };

/**
 * The fields to send to /contest/update (ISSUE-1041). Compares BUILT configs on
 * both sides, never raw drafts, so a field nobody touched is never sent, and a
 * child value a parent switch turned off (a reuse cap once reuse is off, a
 * revive count once revives are unlimited) is sent as null so the server
 * clears it. Null while the edited draft is incomplete.
 */
export function buildContestRulesPatch(input: {
  baseline: typeSurvivorRulesDraft;
  next: typeSurvivorRulesDraft;
}): typeContestRulesPatch | null {
  const nextConfig = buildContestConfig(input.next);
  if (nextConfig === null) return null;
  const baselineConfig = buildContestConfig(input.baseline);
  if (baselineConfig === null) return nextConfig;

  const patch: Record<string, unknown> = {};
  for (const key of CONTEST_RULES_PATCH_KEYS) {
    const nextValue = nextConfig[key] ?? null;
    const baselineValue = baselineConfig[key] ?? null;
    if (
      key === "pickSchedule"
        ? JSON.stringify(nextValue) !== JSON.stringify(baselineValue)
        : nextValue !== baselineValue
    ) {
      patch[key] = nextValue;
    }
  }
  return patch;
}

/** True when any field of the draft differs from the one it started from. */
export function isContestRulesDraftDirty(input: {
  baseline: typeSurvivorRulesDraft;
  next: typeSurvivorRulesDraft;
}): boolean {
  const keys = [
    ...new Set([...Object.keys(input.baseline), ...Object.keys(input.next)]),
  ] as (keyof typeSurvivorRulesDraft)[];
  return keys.some((key) =>
    key === "pickSchedule"
      ? JSON.stringify(input.baseline[key] ?? null) !==
        JSON.stringify(input.next[key] ?? null)
      : input.baseline[key] !== input.next[key],
  );
}

/**
 * Mirrors setContestWindow's roundsExist refusal (CONFLICT contestStarted on
 * /contest/update), which stays the enforcement. While the round list is
 * still loading the dates stay locked, so they never flicker editable.
 */
export function areContestDatesLocked(input: {
  roundCount: number | null;
}): boolean {
  return input.roundCount === null || input.roundCount > 0;
}

// The pick schedule editor's form logic (#102), on the same subpath so web
// and mobile import every rules-form helper from one place.
export {
  addPickRange,
  formatPickScheduleDay,
  PICK_SCHEDULE_ERROR_SUMMARY,
  pickRangeFieldErrors,
  pickRangeSummary,
  setPickRangeStart,
  shiftPickScheduleDay,
  splitForNewRange,
  type typePickRangeFieldErrors,
} from "./pickScheduleDraft";
